import { CartItem, IAvailableItem, LocalCart } from '~/types/cart';
import { CollectionByHandle } from '~/types/dto/collectionsDto';
import { VariantDto } from '~/types/dto/productDto';
import { Product } from '~/types/product';

type ProductWithSelectedSizes = Product & {
  selectedSize: string;
  selectedColor: string;
};

export class CartActions {
  static removeCartItem(cart: LocalCart, index: number) {
    const newCartItemsState = [...cart.lineItems];

    if (index !== -1) {
      newCartItemsState.splice(index, 1);
    }

    return { lineItems: newCartItemsState };
  }

  static addCartItem(
    cart: LocalCart,
    product: ProductWithSelectedSizes,
    id: string,
    handlePath: string,
  ) {
    const newCartItems = [...cart.lineItems];
    const cartItemIdx = newCartItems.findIndex(
      (cartItem) => cartItem.variantId === id,
    );

    if (cartItemIdx === -1) {
      newCartItems.push({
        color: product.selectedColor,
        image: product.images[0],
        price: parseInt(product.prices.maxPrice, 10),
        title: product.title,
        quantity: 1,
        variantId: id,
        size: product.selectedSize,
        handle: handlePath,
      });
    } else {
      const cartItemCopy = { ...newCartItems[cartItemIdx] };
      cartItemCopy.quantity += 1;
      newCartItems[cartItemIdx] = cartItemCopy;
    }

    return { lineItems: newCartItems };
  }

  static decreaseItemQuantity(cart: LocalCart, productId: string) {
    const targetIdx = cart.lineItems.findIndex(
      (cartItem) => cartItem.variantId === productId,
    );

    if (targetIdx === -1) {
      return cart;
    }

    const newCartItems = [...cart.lineItems];
    const cartItemCopy = { ...newCartItems[targetIdx] };

    if (cartItemCopy.quantity - 1 === 0) {
      newCartItems.splice(targetIdx, 1);
    } else {
      cartItemCopy.quantity -= 1;
      newCartItems[targetIdx] = cartItemCopy;
    }

    return { lineItems: newCartItems };
  }

  static increaseItemQuantity(cart: LocalCart, productId: string) {
    const targetIdx = cart.lineItems.findIndex(
      (cartItem) => cartItem.variantId === productId,
    );

    if (targetIdx === -1) {
      return cart;
    }

    const newCartItems = [...cart.lineItems];
    const cartItemCopy = { ...newCartItems[targetIdx] };
    cartItemCopy.quantity += 1;
    newCartItems[targetIdx] = cartItemCopy;

    return { lineItems: newCartItems };
  }

  static removeCartItemById(
    cart: LocalCart,
    productId: string,
  ): [LocalCart, number] {
    const targetIdx = cart.lineItems.findIndex(
      (cartItem) => cartItem.variantId === productId,
    );

    if (targetIdx === -1) {
      return [cart, 0];
    }

    const newCartItems = [...cart.lineItems];
    const quantityToRemove = newCartItems[targetIdx].quantity;

    newCartItems.splice(targetIdx, 1);

    return [{ lineItems: newCartItems }, quantityToRemove];
  }
}

export const randomizeCartImproved = (
  cartItems: CartItem[],
  collections: CollectionByHandle[],
) => {
  const usedColors = new Map<string, string[]>();
  const availableColors = new Map<string, Set<string>>();
  const availableVariants = new Map<string, VariantDto[]>();
  const result = [];

  const getAccessorKey = (item: CartItem) =>
    `${item.title}_${item.size}_${item.quantity}`;

  for (const item of cartItems) {
    const collectionProducts = collections.find((collection) => {
      return collection.collectionByHandle.title.includes(item.title);
    })?.collectionByHandle?.products?.edges;

    if (!collectionProducts) {
      continue;
    }

    for (const product of collectionProducts) {
      const variants: VariantDto[] = [];
      const colors: string[] = [];

      product.node.variants.edges.forEach((variant) => {
        const { selectedOptions, availableForSale, quantityAvailable } =
          variant.node;

        const isOfSameSizeAsSelectedSize = selectedOptions.some(
          (option) => option.name === 'Size' && option.value === item.size,
        );

        if (
          isOfSameSizeAsSelectedSize &&
          availableForSale &&
          quantityAvailable
        ) {
          variants.push(variant);
          colors.push(
            variant.node.selectedOptions.find(
              (option) => option.name === 'Color',
            )?.value || '',
          );
        }
      });

      availableVariants.set(getAccessorKey(item), [
        ...(availableVariants.get(getAccessorKey(item)) || []),
        ...variants,
      ]);

      const _colors = new Set<string>();

      colors.forEach((color) => _colors.add(color));

      availableColors.set(
        item.title,
        availableColors.get(item.title)?.union(_colors) || _colors,
      );
    }
  }

  // Order items from the least number of variants to the highest amount
  // So we can work with more data while moving down
  const orderedItems = [...availableVariants.entries()]?.sort(
    ([_keyA, valueA], [_keyB, valueB]) => valueA.length - valueB.length,
  );

  for (const item of orderedItems) {
    const key = item[0];
    const [title, size, _quantity] = key.split('_');
    const quantity = Number(_quantity);
    const selectedVariant = availableVariants.get(key);

    if (!selectedVariant) {
      continue;
    }

    // When we don't have enough variants we add the first one
    if (selectedVariant.length === 1) {
      result.push({
        merchandiseId: selectedVariant.at(0)?.node.id,
        quantity: quantity || 1,
      });
      continue;
    }

    // If a product has a quantity of greater than 1, we split it into different variants
    for (let i = 0; i < quantity; i++) {
      const unusedColors = selectedVariant.filter((variant) => {
        const _usedColors = usedColors.get(title);
        const _availableColors = availableColors.get(title);
        const _availableColorsLength = _availableColors
          ? [..._availableColors.values()]?.length
          : 0;

        // If no colors are used, return the variants
        if (!_usedColors) {
          return true;
        }

        // If all colors have been used, return the variants and allow for duplicates
        if (_usedColors?.length === _availableColorsLength) {
          return true;
        }

        // Return unused color variants
        const variantColors =
          variant.node.selectedOptions.find((option) => option.name === 'Color')
            ?.value || '';

        return !_usedColors.includes(variantColors);
      });

      const selectedColor =
        unusedColors[Math.floor(Math.random() * unusedColors.length)];

      usedColors.set(title, [
        ...(usedColors.get(title) || []),
        selectedColor?.node?.selectedOptions.find(
          (option) => option.name === 'Color',
        )?.value || '',
      ]);

      if (selectedColor?.node.id) {
        result.push({
          merchandiseId: selectedColor?.node.id,
          quantity: 1,
        });
        continue;
      }

      // If we can't get other colors, we select a random variant
      const randomSelectedVariant =
        selectedVariant[Math.floor(Math.random() * selectedVariant.length)];

      if (!randomSelectedVariant) {
        continue;
      }

      // Check if we already have the variant in the result
      const resultIndex = result.findIndex(
        (item) => item.merchandiseId === randomSelectedVariant.node.id,
      );

      // If we don't, let's add it
      if (resultIndex === -1) {
        result.push({
          merchandiseId: randomSelectedVariant?.node.id,
          quantity: 1,
        });
        continue;
      }

      // If we do, let's increase the quantity by 1
      result[resultIndex].quantity += 1;
    }
  }

  return result;
};

export const randomizeCart = (
  cartItems: CartItem[],
  collections: CollectionByHandle[],
): { merchandiseId: string; quantity: number }[] => {
  return cartItems.flatMap((item) => {
    // Find the collection that matches the cart item title
    const collection = collections.find((collection) =>
      collection.collectionByHandle.title.includes(item.title),
    );

    if (!collection) {
      return [];
    }

    const matchingProducts =
      collection.collectionByHandle.products.edges.filter((product) =>
        product.node.variants.edges.some((variant) => {
          if (!variant.node.availableForSale) {
            return false;
          }

          return variant.node.selectedOptions.some((option) => {
            return option.name === 'Size' && option.value === item.size;
          });
        }),
      );

    if (matchingProducts.length === 0) {
      return [];
    }

    // Shuffle the matching products
    const shuffledProducts = matchingProducts
      .map((value) => ({ value, sort: Math.random() }))
      .sort((a, b) => a.sort - b.sort)
      .map(({ value }) => value);

    const result = [];

    for (let i = 0; i < item.quantity; i++) {
      const productIndex = i % shuffledProducts.length;
      const randomProduct = shuffledProducts[productIndex];

      // Find a variant that matches the size
      const matchingVariant = randomProduct.node.variants.edges.find(
        (variant) =>
          variant.node.selectedOptions.some(
            (option) => option.name === 'Size' && option.value === item.size,
          ),
      );

      if (
        !matchingVariant?.node.availableForSale ||
        !matchingVariant?.node.id
      ) {
        continue;
      }

      result.push({
        merchandiseId: matchingVariant?.node.id,
        quantity: 1,
      });
    }

    return result;
  });
};

export const checkUnAvailableItems = (
  cartItems: CartItem[],
  collections: CollectionByHandle[],
): { collectionName: string; matchingProductsLength: number }[] => {
  const unavailableItems: IAvailableItem[] = cartItems.flatMap((item) => {
    // Find the collection that matches the cart item title
    const collection = collections.find((collection) =>
      collection.collectionByHandle.title.includes(item.title),
    );

    if (!collection) {
      return [];
    }

    // Filter products that have available variants matching the item size
    const matchingProducts =
      collection.collectionByHandle.products.edges.filter((product) =>
        product.node.variants.edges.some(
          (variant) =>
            variant.node.availableForSale &&
            variant.node.selectedOptions.some(
              (option) => option.name === 'Size' && option.value === item.size,
            ),
        ),
      );

    if (matchingProducts.length < item.quantity) {
      return {
        collectionName: collection.collectionByHandle.title,
        matchingProductsLength: matchingProducts.length,
        size: item.size,
      };
    } else {
      return [];
    }
  });
  return unavailableItems;
};

export const isNumberAlreadyInCart = (
  number: number,
  title: string,
  cartItems: CartItem[],
): boolean => {
  const matchingProducts = cartItems.filter((item) => item.title === title);

  const totalQuantity = matchingProducts.reduce(
    (sum, item) => sum + item.quantity,
    0,
  );

  return totalQuantity >= number;
};
